import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Col,
  Button,
  Form,
  Row,
  Breadcrumb,
  Spinner,
  InputGroup,
  Accordion,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import paths from "routes/paths";
import { useTranslation } from "react-i18next";
import {
  fetchRoleAndClaims,
  handleLoadingAndAccess,
} from "../../assets/js/common";
import { useAppContext } from "../../providers/appProvider";
import moment from "moment";
import Select from "react-select";
import MultiSelect from "components/common/multiSelect";
import { getDomainConfig } from "../../config/domainConfig";
import "react-phone-number-input/style.css";
import { getCountries } from "react-phone-number-input";
import { parsePhoneNumber, getExampleNumber, getCountryCallingCode } from "libphonenumber-js/max";
import examples from "libphonenumber-js/examples.mobile.json";
import CustomCountrySelect from "components/common/CustomCountrySelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faIdBadge, faBriefcase, faCogs, faArrowRight, faExclamationTriangle, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { ROLE_NAMES } from "../../config/roleConfig";
import { normalizePhoneForDisplay } from "./../../utils/phoneNormalizer";

const EditUser = () => {
  const { t, i18n } = useTranslation();
  const domainConfig = getDomainConfig();
  const currencySymbol = domainConfig.financialLabels.currencySymbol;

  // Validation Schema
  const createSchema = (originalEmail) =>
    yup
      .object({
        name: yup.string().required(t("nameRequired")), // Name is required
        location: yup.string().nullable(),
        // locationId: yup.string().required(t("locationRequired")),
        locationIds: yup
          .array()
          .min(1, t("branchRequired"))
          .required(t("branchRequired")),
        role: yup.string().required(t("roleRequired")), // Type is required
        status: yup.string().required(t("statusRequired")), // Status is required
        password: yup
          .string()
          .nullable()
          .notRequired()
          .test("password-conditional-validation", function (value) {
            const { path, createError } = this;

            if (!value) return true; // allow empty

            if (value.length < 8) {
              return createError({ path, message: t("passwordMinLength") });
            }

            if (!/[a-zA-Z]/.test(value)) {
              return createError({ path, message: t("passwordLetters") });
            }

            if (!/\d/.test(value)) {
              return createError({ path, message: t("passwordNumber") });
            }

            if (!/[A-Z]/.test(value)) {
              return createError({ path, message: t("passwordUppercaseValidationText") });
            }

            if (!/[^a-zA-Z0-9]/.test(value)) {
              return createError({ path, message: t("passwordSpecialCharacterValidationText") });
            }
            return true;
          }),
        email: yup
          .string()
          .email(t("invalidEmail"))
          .required(t("emailRequired")),

        phoneNumber: yup
          .string()
          .nullable()
          .transform((value) => (value === "" ? null : value))
          .test("has-country-code", t("enterValidMobileNumber"), function (value) {
            if (!value) return true;
            if (!value.startsWith("+")) return false;
            const digitsOnly = value.replace(/[^\d]/g, "");
            return digitsOnly.length > 0 && digitsOnly.length <= 20;
          }),

        dateOfJoining: yup
          .date()
          .nullable()
          .transform((curr, orig) => (orig === "" ? null : curr)) // Handle empty values and convert them to null
          .typeError(t("invalidDate"))
          .when("employmentType", {
            is: "permanent",
            then: (s) => s.required(t("dateOfJoiningRequired")),
          }),
        hourlyRate: yup
          .number()
          .nullable()
          .transform((v, orig) => (orig === "" ? null : v))
          .typeError(t("invalidRate"))
          .min(0, t("invalidRate"))
          .max(99999.99, t("invalidRate")),
        fixedHours: yup
          .number()
          .nullable()
          .transform((v, orig) => (orig === "" || orig === null || orig === undefined ? null : v))
          .typeError(t("invalidHours"))
          .min(0, t("invalidHours"))
          .max(999.99, t("invalidHours")),
        managerId: yup
          .string()
          .nullable()
          .transform((value) => (value === "" ? null : value))
          .when("role", {
            is: (roleVal) => !!roleVal && !nonMandatoryManagerRoleIds.includes(roleVal),
            then: (s) => s.required(t("managerRequired")),
          }),
        employmentType: yup.string().required(t("employmentTypeRequired")),
        contractStartDate: yup
          .date()
          .nullable()
          .transform((curr, orig) => (orig === "" ? null : curr))
          .when("employmentType", {
            is: "contract",
            then: (s) => s.required(t("contractStartRequired")),
          }),
        contractEndDate: yup
          .date()
          .nullable()
          .transform((curr, orig) => (orig === "" ? null : curr))
          .when("employmentType", {
            is: "contract",
            then: (s) =>
              s
                .required(t("contractEndRequired"))
                .min(yup.ref("contractStartDate"), t("contractEndAfterStart")),
          }),
        contractNotes: yup.string().nullable(),
        salary: yup
          .number()
          .nullable()
          .transform((v, orig) => (orig === "" ? null : v)),
        salaryPeriod: yup
          .string()
          .nullable(),
      })
      .required(); // Entire schema is required

  const locationData = useLocation();
  const userIdRaw = locationData.pathname.split("/")[2];
  const userId = userIdRaw.toUpperCase();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const dayKeys = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  const [loading, setLoading] = useState(false);
  const [dateOfJoining, setDateOfJoining] = useState(null);
  const [isJoinDateOpen, setIsJoinDateOpen] = useState(false);
  const [managers, setManagers] = useState([]);
  const [isContractStartOpen, setIsContractStartOpen] = useState(false);
  const [isContractEndOpen, setIsContractEndOpen] = useState(false);
  const [userData, setUserData] = useState();
  const domainDefaultCountryCode = (domainConfig.defaultPhoneCountry || "in").toUpperCase();

  const detectedCountryCode = useMemo(() => {
    const raw = userData?.PhoneNumber;
    if (!raw) return null;
    try {
      const normalized = normalizePhoneForDisplay(raw, domainDefaultCountryCode);
      const parsed = parsePhoneNumber(normalized);
      return parsed?.country || null;
    } catch {
      return null;
    }
  }, [userData, domainDefaultCountryCode]);

  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState(domainDefaultCountryCode);

  useEffect(() => {
    if (detectedCountryCode) {
      setSelectedPhoneCountry(detectedCountryCode);
    }
  }, [detectedCountryCode]);

  const defaultCountryCode = selectedPhoneCountry;

  const phonePlaceholder = useMemo(() => {
    const example = getExampleNumber(selectedPhoneCountry, examples);
    return example ? example.formatNational() : "";
  }, [selectedPhoneCountry]);

  const callingCode = useMemo(() => {
    try {
      return getCountryCallingCode(selectedPhoneCountry);
    } catch {
      return "";
    }
  }, [selectedPhoneCountry]);

  const maxNationalLength = useMemo(() => {
    const example = getExampleNumber(selectedPhoneCountry, examples);
    return example ? example.nationalNumber.length : 15;
  }, [selectedPhoneCountry]);

    const countrySelectOptions = useMemo(() => {
    return getCountries().map((country) => ({
      value: country,
      label: country,
    }));
  }, []);

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [userLocations, setuserLocations] = useState([]);
  const [error, setError] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [roles, setRoles] = useState([]);
  const [hasAccess, setHasAccess] = useState(false);
  const { config } = useAppContext();
  const [isCustomerLinked, setIsCustomerLinked] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [selectedSpecialities, setSelectedSpecialities] = useState([]);

  const nonMandatoryManagerRoleIds = useMemo(
    () =>
      roles
        .filter((role) => role.Name === ROLE_NAMES.APP_OWNER || role.Name === ROLE_NAMES.CLIENT)
        .map((role) => role.RoleId),
    [roles]
  );

  const steps = [
    { id: 1, title: t("personalDetails"), subtitle: t("personalDetailsSubEdit") },
    { id: 2, title: t("accountInformation"), subtitle: t("accountInformationSub") },
    { id: 3, title: t("employmentDetails"), subtitle: t("employmentDetailsSub") },
    { id: 4, title: t("administration"), subtitle: t("administrationSub") },
  ];
  const [currentStep, setCurrentStep] = useState(1);
  const [activeAccordionKey, setActiveAccordionKey] = useState("1");

  const requiredFieldsByStep = {
    1: ["name", "email", "status"],
    2: ["role", "locationIds"],
    3: ["employmentType"],
    4: [],
  };

  const stepFieldsMap = {
    1: ["name", "email", "phoneNumber", "status"],
    2: ["role", "locationIds", "managerId"],
    3: ["dateOfJoining", "employmentType", "contractStartDate", "contractEndDate", "contractNotes"],
    4: ["fixedHours", "hourlyRate", "salary", "salaryPeriod"],
  };

  const methods = useForm({
    mode: "onChange",
    // resolver: yupResolver(schema),
    resolver: yupResolver(createSchema(userData?.Emailaddress)),
    defaultValues: {
      status: "actief",
      country: "Nederland",
      fixedHours: "",
      hourlyRate: "",
      managerId: "",
      employmentType: "permanent",
      contractStartDate: null,
      contractEndDate: null,
      contractNotes: "",
      salary: "",
      salaryPeriod: "monthly",
      weekdays: {
        maandag: false,
        dinsdag: false,
        woensdag: false,
        donderdag: false,
        vrijdag: false,
        zaterdag: false,
        zondag: false,
      },
      locationIds: [],
    },
  });

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
    trigger,
    watch,
  } = methods;

  const watchedValues = watch();

  const computedCompletedSteps = useMemo(() => {
    return Object.keys(requiredFieldsByStep)
      .filter((stepId) => {
        const requiredFields = requiredFieldsByStep[stepId];
        if (requiredFields.length === 0) return true;
        return requiredFields.every((field) => {
          const val = watchedValues[field];
          if (field === "locationIds") return Array.isArray(val) && val.length > 0;
          return val !== undefined && val !== null && val !== "";
        });
      })
      .map(Number);
  }, [watchedValues]);

  const otherStepsCompleted = useMemo(() => {
    return [1, 2, 3, 4]
      .filter((id) => id !== currentStep)
      .every((id) => computedCompletedSteps.includes(id));
  }, [computedCompletedSteps, currentStep]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const fieldsWithErrors = Object.keys(errors);
      trigger(fieldsWithErrors);
    }
  }, [i18n.language]);

  const hashPassword = async (password) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  const checkIfCustomerExists = async (email) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/business/customerRoute/byEmail/${email}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      if (res.data.found) {
        setIsCustomerLinked(true);
      } else {
        console.warn(res.data.message);
        setIsCustomerLinked(false);
      }
    } catch (err) {
      console.error("Error fetching customer:", err);
      setIsCustomerLinked(false);
    }
  };


  const fetchUser = async (userId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      setUserData(response.data);
      const userData = response.data;
      setValue("name", userData.Name);
      setValue("status", userData.Status);
      setValue("email", userData.Emailaddress);
      setValue("phoneNumber", normalizePhoneForDisplay(userData.PhoneNumber, domainDefaultCountryCode));
      setValue("role", userData.Role);
      setValue("locationId", userData.LocationId);
      setSelectedLocationId(userData.LocationId);
      setValue("features", userData.Features);
      setValue("managerId", userData.ManagerId || "");
      setValue("employmentType", userData.EmploymentType || "permanent");
      setValue(
        "contractStartDate",
        userData.ContractStartDate ? moment(userData.ContractStartDate).toDate() : null
      );
      setValue(
        "contractEndDate",
        userData.ContractEndDate ? moment(userData.ContractEndDate).toDate() : null
      );
      setValue("contractNotes", userData.ContractNotes || "");
      setValue(
        "salary",
        userData.Salary != null ? userData.Salary.toString().replace(".", ",") : ""
      );
      setValue("salaryPeriod", userData.SalaryPeriod || "monthly");

      const joiningDate = userData.DateOfJoining
        ? moment(userData.DateOfJoining).toDate()
        : null;

      setDateOfJoining(joiningDate);
      setValue("dateOfJoining", joiningDate);
      setValue(
        "fixedHours",
        userData.FixedHours != null
          ? userData.FixedHours.toString().replace(".", ",")
          : null
      );
      setValue(
        "hourlyRate",
        userData.HourlyRate != null
          ? userData.HourlyRate.toString().replace(".", ",")
          : null
      );

      const parsedWeekdays = userData.Weekdays
        ? JSON.parse(userData.Weekdays) // ["monday","tuesday"]
        : [];

      setSelectedDays(parsedWeekdays);

      // keep RHF in sync (optional but safe)
      dayKeys.forEach(day => {
        setValue(`weekdays.${day}`, parsedWeekdays.includes(day));
      });

      const userSpecialityIds = (userData.specialities || []).map(s => s.SpecialityId);
      setSelectedSpecialities(userSpecialityIds);
      setValue("specialityIds", userSpecialityIds, { shouldValidate: false });

      // Check if this user also exists in customer
      await checkIfCustomerExists(userData.Emailaddress);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setIsLoading(false);
    }
  };

  const fetchLocations = async () => {
    setLoading(true);
    const customerId = localStorage.getItem("customerId");
    if (!customerId) {
      setLoading(false);
      setError("CustomerId is missing.");
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/locationRoute/all`, {
        params: { customerId },
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setuserLocations(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to fetch branches. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecialities = async () => {
    try {
      const customerId = localStorage.getItem("customerId");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/specialityRoute/byCustomer/${customerId}`,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
      setSpecialities(response.data);
    } catch (err) {
      console.error("Error fetching specialities:", err);
    }
  };

  const roleTranslations = {
    [ROLE_NAMES.APP_OWNER]: t("appOwnerText"),
    [ROLE_NAMES.ADMIN]: t("adminText"),
    [ROLE_NAMES.CLIENT]: t("client"),
    [ROLE_NAMES.EMPLOYEE]: t("employeeText"),
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/roleRoute/roles`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setRoles(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "users",
      claimValue: "edit",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  useEffect(() => {
    fetchUser(userId);
    fetchLocations();
    fetchRoles();
    fetchSpecialities();
  }, [config.location]);

  useEffect(() => {
    if (!userData) return;
    const fetchManagers = async () => {
      try {
        const locationIdStored = localStorage.getItem("LocationId");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/eligibleManagers`,
          {
            params: {
              locationId: locationIdStored,
              excludeUserId: userId,
              excludeUserRole: userData?.Role,
              targetRoleId: watchedValues.role || userData?.Role,
            },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          }
        );
        setManagers(response.data);
      } catch (err) {
        console.error("Error fetching managers:", err);
      }
    };
    fetchManagers();
  }, [userId, userData, watchedValues.role]);

  useEffect(() => {
    if (userData?.locations?.length && userLocations.length > 0) {
      const locationIds = userData.locations.map(loc => loc.LocationId);
      setSelectedLocations(locationIds);
      setValue("locationIds", locationIds, { shouldValidate: true });
    }
  }, [userData, userLocations]);

  const handleDiscard = () => {
    reset();
    navigate(`${paths.userDetails.replace(":userId", userId.toLowerCase())}`);
  };

  const goToStep = (stepId) => {
    if (stepId === currentStep) return;
    setCurrentStep(stepId);
    setActiveAccordionKey(String(stepId));
  };

  const handleNext = async () => {
    const valid = await trigger(stepFieldsMap[currentStep]);
    if (!valid) {
      toast.error(t("pleaseFillRequiredFields"), { position: "top-right", autoClose: 3000 });
      return;
    }
    setCurrentStep((step) => {
      const nextStep = Math.min(step + 1, 4);
      setActiveAccordionKey(String(nextStep));
      return nextStep;
    });
  };

  const handleSave = async () => {
    await trigger(stepFieldsMap[currentStep]);
    handleSubmit(onSubmit, onInvalid)();
  };

  const handleBack = () => {
    setCurrentStep((step) => {
      const prevStep = Math.max(step - 1, 1);
      setActiveAccordionKey(String(prevStep));
      return prevStep;
    });
  };

  const onInvalid = (formErrors) => {
    const errorFields = Object.keys(formErrors);
    if (errorFields.length === 0) return;

    const stepWithError = Object.keys(stepFieldsMap).find((stepId) =>
      stepFieldsMap[stepId].some((field) => errorFields.includes(field))
    );

    if (stepWithError) {
      setCurrentStep(Number(stepWithError));
      setActiveAccordionKey(stepWithError);
    }

    toast.error(t("pleaseFillRequiredFields"), {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      const userId = userData.UserId;
      const customerId = userData.CustomerId;

      const formattedDate = data.dateOfJoining
        ? moment(data.dateOfJoining).format("YYYY-MM-DD")
        : null;

      let activeDateTime =
        data.status === "0" ? moment.utc().format() : userData.ActiveDateTime;

      let inactiveDateTime =
        data.status === "1" ? moment.utc().format() : userData.InactiveDateTime;

      const weekdaysArray = selectedDays.length > 0 ? selectedDays : null;
      const performedBy = localStorage.getItem("UserId");

      const updatedFormData = {
        ...data,
        weekdays: weekdaysArray,
        dateOfJoining: formattedDate,
        managerId: data.managerId || null,
        employmentType: data.employmentType,
        contractStartDate: data.employmentType === "contract" && data.contractStartDate
          ? moment(data.contractStartDate).format("YYYY-MM-DD")
          : null,
        contractEndDate: data.employmentType === "contract" && data.contractEndDate
          ? moment(data.contractEndDate).format("YYYY-MM-DD")
          : null,
        contractNotes: data.employmentType === "contract" ? data.contractNotes || null : null,
        salary: data.employmentType === "permanent" ? data.salary : null,
        salaryPeriod: data.employmentType === "permanent" ? data.salaryPeriod : null,
        userId,
        activeDateTime,
        inactiveDateTime,
        performedBy
      };

      if (data.password) {
        updatedFormData.password = await hashPassword(data.password);
      } else {
        updatedFormData.password = userData.Password;
      }

      // Update user
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/userRoute/updateUser/${userId}`,
        updatedFormData,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

      // If user exists as customer → also update customer
      const customerRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/business/customerRoute/byEmail/${data.email}`,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

      if (customerRes.data.found) {
        const customer = customerRes.data;
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/business/customerRoute/updateUserCustomer/${customer.CustomerId}`,
          {
            CustomerName: data.name,
            PhoneNumber: data.phoneNumber,
          },
          {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret,
            },
          }
        );
      }

      //  Update user locations (DELETE + INSERT)
      await axios.put(
        `${process.env.REACT_APP_API_URL}/userLocationsRoute/updateUserLocations`,
        {
          UserId: userId,
          CustomerId: customerId,
          LocationIds: selectedLocations, // from MultiSelect
          performedBy
        },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

      // Update user specialities (DELETE + INSERT)
      await axios.post(
        `${process.env.REACT_APP_API_URL}/userSpecialitiesRoute/save`,
        {
          UserId: userId,
          CustomerId: customerId,
          SpecialityIds: data.specialityIds || [],
        },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

      toast.success(t("userUpdated"), {
        position: "top-right",
        autoClose: 5000,
      });

      navigate(`${paths.userDetails.replace(":userId", userId.toLowerCase())}`);
    } catch (error) {
      console.error("Update Error:", error);
      const dependents = error.response?.data?.dependents;
      if (dependents?.length > 0) {
        const MAX_NAMES_SHOWN = 5;
        const visibleNames = dependents.slice(0, MAX_NAMES_SHOWN).join(", ");
        const remainingCount = dependents.length - MAX_NAMES_SHOWN;

        const message = remainingCount > 0
          ? t("userIsManagerForOthersCannotChangeRoleTruncated", {
            count: dependents.length,
            names: visibleNames,
            remaining: remainingCount,
          })
          : t("userIsManagerForOthersCannotChangeRole", {
            count: dependents.length,
            names: visibleNames,
          });

        toast.error(message, {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (error.response?.data?.messageKey || error.response?.data?.message) {
        const key = error.response.data.messageKey || error.response.data.message;
        toast.error(t(key), {
          position: "top-right",
          autoClose: 5000,
        });
      } else {
        toast.error(t("errorOccurred"), {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadingOrRedirect = handleLoadingAndAccess(isLoading, hasAccess);

  if (loadingOrRedirect) {
    return loadingOrRedirect; // Render the loading spinner or handle navigation
  }

  const toggleDay = (dayKey) => {
    setSelectedDays((prev) => {
      const newSelected = prev.includes(dayKey)
        ? prev.filter((d) => d !== dayKey)
        : [...prev, dayKey];

      // Update the individual checkbox value
      setValue(`weekdays.${dayKey}`, !prev.includes(dayKey));
      return newSelected;
    });
  };

  return (
    <FormProvider {...methods}>
      <Row className="g-3 mb-3">
        <Col lg={12} xxl={12}>
          <Card className="mb-3 h-100">
            <Background
              image={corner4}
              className="bg-card"
              style={{
                borderTopRightRadius: "0.375rem",
                borderBottomRightRadius: "0.375rem",
              }}
            />
            <Card.Body className="position-relative pe-4 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5 className="mb-2 mb-md-0">{t("editUser")}</h5>
                  <Breadcrumb>
                    <Breadcrumb.Item
                      as={Link}
                      to="/users"
                      className="fw-semi-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("users")}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      onClick={() =>
                        navigate(
                          `${paths.userDetails.replace(":userId", userId.toLowerCase())}`
                        )
                      }
                      className="fw-semi-bold mb-1 fs-9 cursor-pointer data-font-colour"
                      active
                    >
                      {userData ? userData.Name : "Details"}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      className="fw-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("edit")}
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {isLoading || !userData ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </Spinner>
        </div>
      ) : (
        <Form onSubmit={(e) => e.preventDefault()}>
          <Row className="g-3 align-items-stretch">
            {/* Left Sidebar — copy verbatim from NewUser.jsx */}
            <Col xs="12" lg="3" xl="2" className="rf-left-panel step-sidebar-col">
              <Card className="mb-3 h-100 step-row-container">
                <Card.Body>
                  {steps.map((step, index) => (
                    <div
                      key={step.id}
                      lang={i18n.language}
                      className={`d-flex gap-2 pointer p-2 rounded step-row ${currentStep === step.id ? "step-row-active" : ""}`}
                      onClick={() => goToStep(step.id)}
                    >
                      <div className="d-flex flex-column align-items-center step-circle-col">
                        <span className={`step-circle ${currentStep === step.id ? "active" : ""} ${computedCompletedSteps.includes(step.id) ? "completed" : ""}`}>
                          {step.id}
                        </span>
                        {index < steps.length - 1 && (
                          <div className={`step-connector-line ${computedCompletedSteps.includes(step.id) ? "completed" : ""} ${currentStep === step.id ? "active" : ""}`} />
                        )}
                      </div>
                      <div className="step-text-col">
                        <h5 className={`fw-medium ${currentStep === step.id ? "text-primary" : ""}`}>{step.title}</h5>
                        <small className="mt-2 text-secondary">{step.subtitle}</small>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>
            {/* Right side accordion */}
            <Col xs="12" lg="9" xl="10" className="rf-right-panel rf-form-col">
              <Accordion activeKey={activeAccordionKey} onSelect={(key) => { if (key) goToStep(Number(key)); }} className="rf-accordion">
                <Accordion.Item eventKey="1" className="rf-accordion-item">
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center">
                      <span className="rf-header-icon bg-client"><FontAwesomeIcon icon={faUser} /></span>
                      <span className="rf-header-text">
                        <span>{t("personalDetails")}</span>
                        <span className="rf-header-subtitle">{t("personalDetailsSubEdit")}</span>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md="4" lg="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("name")}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            size="md"
                            {...register("name")}
                            isInvalid={!!errors.name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.name?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md="6" lg="5">
                        <Form.Group>
                          <Form.Label className="data-font-colour data-font-size">
                            {t("emailAddress")}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="email"
                            {...register("email")}
                            isInvalid={!!errors.email}
                            disabled={isCustomerLinked}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md="4" lg="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("mobileNumber")}
                          </Form.Label>
                          <Controller
                            name="phoneNumber"
                            control={control}
                            defaultValue=""
                            render={({ field: { onChange, value, ref } }) => {
                              const prefix = `+${callingCode}`;
                              const displayValue =
                                value && value.startsWith(prefix)
                                  ? value.slice(prefix.length)
                                  : value || "";

                              return (
                                <div className={`phone-field-wrapper ${errors.phoneNumber ? "is-invalid" : ""}`}>
                                  <span className="phone-country-select-slot">
                                    <CustomCountrySelect
                                      value={selectedPhoneCountry}
                                      options={countrySelectOptions}
                                      onChange={(country) => {
                                        if (!country) return;
                                        const oldPrefix = `+${callingCode}`;
                                        const rawDigits =
                                          value && value.startsWith(oldPrefix)
                                            ? value.slice(oldPrefix.length)
                                            : value || "";
                                        let newCallingCode = "";
                                        try {
                                          newCallingCode = getCountryCallingCode(country);
                                        } catch {
                                          newCallingCode = "";
                                        }
                                        setSelectedPhoneCountry(country);
                                        onChange(rawDigits ? `+${newCallingCode}${rawDigits}` : "");
                                      }}
                                    />
                                  </span>
                                  <span className="phone-code-display">+{callingCode}</span>
                                  <input
                                    ref={ref}
                                    type="text"
                                    className="phone-number-input"
                                    value={displayValue}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      onChange(raw ? `${prefix}${raw}` : "");
                                    }}
                                    placeholder={phonePlaceholder}
                                    maxLength={25}
                                    name="phoneNumber"
                                  />
                                </div>
                              );
                            }}
                          />

                          {errors.phoneNumber && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                              {errors.phoneNumber.message}
                            </Form.Control.Feedback>
                          )}

                          {errors.phoneNumber && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                              {errors.phoneNumber.message}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md="4">
                        <Form.Label className="data-font-size data-font-colour d-block">
                          {t("status")}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="d-flex gap-3">
                          <Form.Check
                            type="radio"
                            id="status-active"
                            label={t("active")}
                            value="0"
                            {...register("status")}
                            isInvalid={!!errors.status}
                          />
                          <Form.Check
                            type="radio"
                            id="status-inactive"
                            label={t("deactive")}
                            value="1"
                            {...register("status")}
                            isInvalid={!!errors.status}
                          />
                        </div>
                        {errors.status && (
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {errors.status.message}
                          </Form.Control.Feedback>
                        )}
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2" className="rf-accordion-item">
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center">
                      <span className="rf-header-icon bg-address"><FontAwesomeIcon icon={faIdBadge} /></span>
                      <span className="rf-header-text">
                        <span>{t("accountInformation")}</span>
                        <span className="rf-header-subtitle">{t("accountInformationSub")}</span>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md="4" lg="4" className="position-relative z-3">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("roleText")}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Controller
                            name="role"
                            control={control}
                            rules={{ required: t("roleRequired") }} // Customize validation message
                            render={({ field, fieldState: { error } }) => {
                              const options = roles.map((role) => ({
                                value: role.RoleId,
                                label: roleTranslations[role.Name] || role.Name,
                              }));

                              return (
                                <>
                                  <Select
                                    key={i18n.language}
                                    options={options}
                                    isClearable
                                    {...field}
                                    value={options.find((opt) => opt.value === field.value) || null}
                                    onChange={(selected) => {
                                      const selectedValue = selected ? selected.value : "";
                                      field.onChange(selectedValue);
                                      setValue("role", selectedValue); // Optional: if syncing elsewhere
                                    }}
                                    isDisabled={isCustomerLinked}
                                    onBlur={field.onBlur}
                                    className={`custom-select-container ${error ? "is-invalid" : ""}`}
                                    classNamePrefix="custom-select"
                                    placeholder={t("select")}
                                    noOptionsMessage={() => t("noOptionsAvailable")}
                                  />
                                  {error && (
                                    <div className="invalid-feedback d-block">{error.message}</div>
                                  )}
                                </>
                              );
                            }}
                          />
                        </Form.Group>
                      </Col>

                      <Col md="6" lg="6" xl="5">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("branch")}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <MultiSelect
                            isMulti
                            closeMenuOnSelect={false}
                            noOptionsMessage={() => t("noOptionsAvailable")}
                            options={userLocations.map(location => ({
                              value: location.LocationId,
                              label: location.Name,
                            }))}

                            value={userLocations
                              .filter(loc => selectedLocations.includes(loc.LocationId))
                              .map(loc => ({
                                value: loc.LocationId,
                                label: loc.Name,
                              }))}

                            classNamePrefix="react-select"
                            className={errors.locationIds ? "is-invalid" : ""}

                            onChange={(selected) => {
                              const values = selected ? selected.map(item => item.value) : [];
                              setSelectedLocations(values);
                              setValue("locationIds", values, { shouldValidate: true });
                            }}
                          />

                          {errors.locationIds && (
                            <div className="invalid-feedback d-block">
                              {errors.locationIds.message}
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md="4" lg="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("manager")}
                            {watchedValues.role && !nonMandatoryManagerRoleIds.includes(watchedValues.role) && (
                              <span className="text-danger">*</span>
                            )}
                          </Form.Label>
                          <Controller
                            name="managerId"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                              <>
                                <Select
                                  options={managers.map((m) => ({ value: m.UserId, label: m.Name }))}
                                  isClearable
                                  value={managers.map((m) => ({ value: m.UserId, label: m.Name })).find((opt) => opt.value === field.value) || null}
                                  onChange={async (selected) => {
                                    const selectedValue = selected ? selected.value : "";
                                    field.onChange(selectedValue);
                                    methods.clearErrors("managerId");

                                    if (selectedValue) {
                                      try {
                                        const res = await axios.get(
                                          `${process.env.REACT_APP_API_URL}/userRoute/checkManagerCircular`,
                                          {
                                            params: { userId, managerId: selectedValue, role: watch("role") },
                                            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
                                          }
                                        );
                                        if (!res.data.valid) {
                                          methods.setError("managerId", { type: "manual", message: t(res.data.message) });
                                        }
                                      } catch (err) {
                                        console.error("Error checking manager circular:", err);
                                      }
                                    }
                                  }}
                                  className={`custom-select-container ${error ? "is-invalid" : ""}`}
                                  classNamePrefix="custom-select"
                                  placeholder={t("select")}
                                  noOptionsMessage={() => t("noOptionsAvailable")}
                                />
                                {error && (
                                  <div className="invalid-feedback d-block text-nowrap">{error.message}</div>
                                )}
                              </>
                            )}
/>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="3" className="rf-accordion-item">
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center">
                      <span className="rf-header-icon bg-contact"><FontAwesomeIcon icon={faBriefcase} /></span>
                      <span className="rf-header-text">
                        <span>{t("employmentDetails")}</span>
                        <span className="rf-header-subtitle">{t("employmentDetailsSub")}</span>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md="2" lg="2">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour text-nowrap">
                            {t("employmentType")}<span className="text-danger">*</span>
                          </Form.Label>
                          <div className="d-flex gap-3">
                            <Form.Check
                              type="radio"
                              id="employmentType-permanent"
                              name="employmentType"
                              label={t("permanent")}
                              checked={watch("employmentType") === "permanent"}
                              onChange={() => setValue("employmentType", "permanent", { shouldValidate: true })}
                            />
                            <Form.Check
                              type="radio"
                              id="employmentType-contract"
                              name="employmentType"
                              label={t("contract")}
                              checked={watch("employmentType") === "contract"}
                              onChange={() => setValue("employmentType", "contract", { shouldValidate: true })}
                            />
                          </div>
                          {errors.employmentType && (
                            <div className="invalid-feedback d-block">{errors.employmentType.message}</div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    {watch("employmentType") === "permanent" && (
                      <Row className="mb-3">
                        <Col md="3">
                          <Form.Group>
                            <Form.Label className="data-font-size data-font-colour text-nowrap">
                              {t("employeeJoinDate")}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Controller
                              name="dateOfJoining"
                              control={methods.control}
                              render={({ field }) => (
                                <div className={`position-relative date-picker-wrapper ${errors.dateOfJoining ? "is-invalid-wrapper" : ""}`}>
                                  <DatePicker
                                    selected={field.value}
                                    onChange={(date) => {
                                      field.onChange(date);
                                      setIsJoinDateOpen(false);
                                    }}
                                    open={isJoinDateOpen}
                                    onInputClick={() => setIsJoinDateOpen(true)}
                                    onClickOutside={() => setIsJoinDateOpen(false)}
                                    dateFormat="dd-MM-yyyy"
                                    className={`form-control pe-2 py-2 ${errors.dateOfJoining ? "is-invalid" : ""}`}
                                    placeholderText="dd-mm-yyyy"
                                  />
                                  <span
                                    className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 lh-1 cursor-pointer z-index-1"
                                    onClick={() => setIsJoinDateOpen((prev) => !prev)}
                                  >
                                    <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                  </span>
                                </div>
                              )}
                            />
                            {errors.dateOfJoining && (
                              <Form.Control.Feedback type="invalid" className="d-block">
                                {errors.dateOfJoining.message}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md="5">
                          <Form.Group>
                            <Form.Label className="data-font-size data-font-colour">{t("speciality")}</Form.Label>
                            <Controller
                              name="specialityIds"
                              control={control}
                              defaultValue={[]}
                              render={({ field }) => (
                                <MultiSelect
                                  isMulti
                                  closeMenuOnSelect={false}
                                  noOptionsMessage={() => t("noOptionsAvailable")}
                                  options={specialities.map(s => ({ value: s.SpecialityId, label: t(s.Name) }))}
                                  value={specialities
                                    .filter(s => (field.value || []).includes(s.SpecialityId))
                                    .map(s => ({ value: s.SpecialityId, label: t(s.Name) }))}
                                  classNamePrefix="react-select"
                                  onChange={(selected) => field.onChange(selected ? selected.map(o => o.value) : [])}
                                />
                              )}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    {watch("employmentType") === "contract" && (
                      <>
                        <Row className="mb-3">
                          <Col md="3">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour">
                                {t("contractStartDate")}<span className="text-danger">*</span>
                              </Form.Label>
                              <Controller
                                control={control}
                                name="contractStartDate"
                                render={({ field }) => (
                                  <div className={`position-relative date-picker-wrapper ${errors.contractStartDate ? "is-invalid-wrapper" : ""}`}>
                                    <DatePicker
                                      selected={field.value}
                                      onChange={(date) => {
                                        field.onChange(date);
                                        setIsContractStartOpen(false);
                                        const currentEndDate = watch("contractEndDate");
                                        if (currentEndDate) {
                                          if (date && currentEndDate <= date) {
                                            setValue("contractEndDate", null);
                                          } else {
                                            trigger("contractEndDate");
                                          }
                                        }
                                      }}
                                      open={isContractStartOpen}
                                      onInputClick={() => setIsContractStartOpen(true)}
                                      onClickOutside={() => setIsContractStartOpen(false)}
                                      dateFormat="dd-MM-yyyy"
                                      className={`form-control pe-2 py-2 ${errors.contractStartDate ? "is-invalid" : ""}`}
                                      placeholderText="dd-mm-yyyy"
                                    />
                                    <span
                                      className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 lh-1 cursor-pointer z-index-1"
                                      onClick={() => setIsContractStartOpen((prev) => !prev)}
                                    >
                                      <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                    </span>
                                  </div>
                                )}
                              />
                              {errors.contractStartDate && (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                  {errors.contractStartDate.message}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md="3">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour">
                                {t("contractEndDate")}<span className="text-danger">*</span>
                              </Form.Label>
                              <Controller
                                control={control}
                                name="contractEndDate"
                                render={({ field }) => (
                                  <div className={`position-relative date-picker-wrapper ${errors.contractEndDate ? "is-invalid-wrapper" : ""}`}>
                                    <DatePicker
                                      selected={field.value}
                                      onChange={(date) => {
                                        field.onChange(date);
                                        setIsContractEndOpen(false);
                                      }}
                                      open={isContractEndOpen}
                                      onInputClick={() => setIsContractEndOpen(true)}
                                      onClickOutside={() => setIsContractEndOpen(false)}
                                      dateFormat="dd-MM-yyyy"
                                      className={`form-control pe-2 py-2 ${errors.contractEndDate ? "is-invalid" : ""}`}
                                      placeholderText="dd-mm-yyyy"
                                      minDate={watch("contractStartDate")}
                                    />
                                    <span
                                      className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 lh-1 cursor-pointer z-index-1"
                                      onClick={() => setIsContractEndOpen((prev) => !prev)}
                                    >
                                      <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                    </span>
                                  </div>
                                )}
                              />
                              {errors.contractEndDate && (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                  {errors.contractEndDate.message}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md="5">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour">{t("speciality")}</Form.Label>
                              <Controller
                                name="specialityIds"
                                control={control}
                                defaultValue={[]}
                                render={({ field }) => (
                                  <MultiSelect
                                    isMulti
                                    closeMenuOnSelect={false}
                                    noOptionsMessage={() => t("noOptionsAvailable")}
                                    options={specialities.map(s => ({ value: s.SpecialityId, label: t(s.Name) }))}
                                    value={specialities
                                      .filter(s => (field.value || []).includes(s.SpecialityId))
                                      .map(s => ({ value: s.SpecialityId, label: t(s.Name) }))}
                                    classNamePrefix="react-select"
                                    onChange={(selected) => field.onChange(selected ? selected.map(o => o.value) : [])}
                                  />
                                )}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md="6">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour">
                                {t("contractNotes")}
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={1}
                                placeholder={t("contractNotesPlaceholder")}
                                className="auto-expand-textarea"
                                {...register("contractNotes")}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="4" className="rf-accordion-item">
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center">
                      <span className="rf-header-icon bg-invoice"><FontAwesomeIcon icon={faCogs} /></span>
                      <span className="rf-header-text">
                        <span>{t("administration")}</span>
                        <span className="rf-header-subtitle">{t("administrationSub")}</span>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col  md="2" lg="3" xl="3" className="mb-3 mb-lg-0">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour text-nowrap">
                            {t("fixedHours")}
                          </Form.Label>
                          <Form.Control
                            type="text"
                            inputMode="decimal"
                            {...register("fixedHours", {
                              required: false,
                              pattern: {
                                value: /^[0-9]*[,]?[0-9]{0,2}$/,
                                message:
                                  t("invalidHours") ||
                                  "Only numbers and a comma or dot are allowed",
                              },
                              setValueAs: (value) =>
                                typeof value === "string"
                                  ? parseFloat(value.replace(",", "."))
                                  : value,
                            })}
                            onKeyDown={(e) => {
                              const allowedKeys = [
                                "Backspace",
                                "Delete",
                                "ArrowLeft",
                                "ArrowRight",
                                "Tab",
                              ];
                              const isNumber = /^[0-9]$/.test(e.key);
                              const isCommaOrDot = e.key === "," || e.key === ".";
                              const value = e.currentTarget.value;

                              if (
                                !isNumber &&
                                !allowedKeys.includes(e.key) &&
                                !(
                                  isCommaOrDot &&
                                  !value.includes(",") &&
                                  !value.includes(".")
                                )
                              ) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>

                      {watch("employmentType") === "contract" && (
                        <Col md="3" lg="3">
                          <Form.Group>
                            <Form.Label className="data-font-size data-font-colour text-nowrap">
                              {t("hourlyRate")}
                            </Form.Label>
                            <InputGroup>
                              <InputGroup.Text>{currencySymbol}</InputGroup.Text>
                              <Form.Control
                                type="text"
                                inputMode="decimal"
                                {...register("hourlyRate", {
                                  setValueAs: (value) =>
                                    typeof value === "string" && value.trim() !== ""
                                      ? parseFloat(value.replace(",", "."))
                                      : undefined,
                                })}
                                isInvalid={!!errors.hourlyRate}
                                onKeyDown={(e) => {
                                  const allowedKeys = [
                                    "Backspace",
                                    "Delete",
                                    "ArrowLeft",
                                    "ArrowRight",
                                    "Tab",
                                  ];
                                  const isNumber = /^[0-9]$/.test(e.key);
                                  const isComma = e.key === ",";
                                  const value = e.currentTarget.value;

                                  if (
                                    !isNumber &&
                                    !allowedKeys.includes(e.key) &&
                                    !(isComma && !value.includes(","))
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.hourlyRate?.message}
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group>
                        </Col>
                      )}

                      {watch("employmentType") === "permanent" && (
                        <>
                          <Col md="3" lg="3">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour text-nowrap">
                                {t("salary")}
                              </Form.Label>
                              <InputGroup>
                                <InputGroup.Text>{currencySymbol}</InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  inputMode="decimal"
                                  placeholder={("45000")}
                                  {...register("salary", {
                                    setValueAs: (value) => {
                                      if (value === null || value === undefined || value === "") return null;
                                      const parsed = parseFloat(String(value).replace(",", "."));
                                      return isNaN(parsed) ? null : parsed;
                                    },
                                  })}
                                  isInvalid={!!errors.salary}
                                  onKeyDown={(e) => {
                                    const allowedKeys = [
                                      "Backspace",
                                      "Delete",
                                      "ArrowLeft",
                                      "ArrowRight",
                                      "Tab",
                                    ];
                                    const isNumber = /^[0-9]$/.test(e.key);
                                    const isComma = e.key === ",";
                                    const value = e.currentTarget.value;

                                    if (
                                      !isNumber &&
                                      !allowedKeys.includes(e.key) &&
                                      !(isComma && !value.includes(","))
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.salary?.message}
                                </Form.Control.Feedback>
                              </InputGroup>
                            </Form.Group>
                          </Col>

                          <Col md="3" lg="3">
                            <Form.Group>
                              <Form.Label className="data-font-size data-font-colour">
                                {t("salaryPeriod")}
                              </Form.Label>
                              <Controller
                                name="salaryPeriod"
                                control={control}
                                render={({ field, fieldState: { error } }) => {
                                  const salaryPeriodOptions = [
                                    { value: "monthly", label: t("salaryPerMonth") },
                                    { value: "weekly", label: t("salaryPerWeek") },
                                  ];
                                  return (
                                    <>
                                      <Select
                                        {...field}
                                        options={salaryPeriodOptions}
                                        value={
                                          salaryPeriodOptions.find((opt) => opt.value === field.value) || null
                                        }
                                        onChange={(selected) => field.onChange(selected ? selected.value : "")}
                                        onBlur={field.onBlur}
                                        className={`custom-select-container ${error ? "is-invalid" : ""}`}
                                        classNamePrefix="custom-select"
                                        placeholder={t("select")}
                                        noOptionsMessage={() => t("noOptionsAvailable")}
                                      />
                                      {error && (
                                        <div className="invalid-feedback d-block">{error.message}</div>
                                      )}
                                    </>
                                  );
                                }}
                              />
                            </Form.Group>
                          </Col>
                        </>
                      )}
                    </Row>

                    <Row>
                      <Col md="12" className="mb-3">
                        <label className="data-font-size data-font-colour">{t("weekdays")}</label>
                        <div className="d-flex flex-wrap gap-2">
                          {dayKeys.map((dayKey) => (
                            <div key={dayKey}>
                              <input
                                type="checkbox"
                                {...register(`weekdays.${dayKey}`)}
                                checked={selectedDays.includes(dayKey)}
                                readOnly
                                hidden
                              />
                              <Button
                                variant={
                                  selectedDays.includes(dayKey)
                                    ? "success"
                                    : "outline-secondary"
                                }
                                className="weekday-btn"
                                onClick={() => toggleDay(dayKey)}
                              >
                                {t(`days.${dayKey}`)}
                              </Button>
                            </div>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              {/* Footer buttons */}
              <Card className="mt-2">
                <Card.Body>
                  {isCustomerLinked && (
                    <Row className="mb-2">
                      <Col>
                        {/* MOBILE — icon and tooltip */}
                        <OverlayTrigger
                          placement="top"
                          trigger="click"
                          rootClose
                          overlay={
                            <Tooltip id="customer-linked-tooltip">
                              {t("userLinkedToCustomerCannotEditImportantFields")}
                            </Tooltip>
                          }
                        >
                          <Button
                            variant="falcon-default"
                            size="sm"
                            className="text-warning border-0 p-1 d-md-none"
                            type="button"
                            aria-label={t("userLinkedToCustomerCannotEditImportantFields")}
                          >
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                          </Button>
                        </OverlayTrigger>

                        {/* DESKTOP — full inline text */}
                        <div className="text-warning small d-none d-md-block">
                          {t("userLinkedToCustomerCannotEditImportantFields")}
                        </div>
                      </Col>
                    </Row>
                  )}
                  <Row className="align-items-center justify-content-between g-2">
                    <Col xs="auto">
                      <Button variant="falcon-default" size="sm" type="button" onClick={handleDiscard}>
                        {t("cancel")}
                      </Button>
                    </Col>

                    {/* Buttons */}
                    <Col xs="auto" className="ms-auto">
                      {currentStep > 1 && (
                        <Button variant="falcon-default" size="sm" className="me-3" type="button" onClick={handleBack}>
                          {t("back")}
                        </Button>
                      )}
                      {currentStep < 4 && !otherStepsCompleted ? (
                        <Button variant="primary" size="sm" type="button" onClick={handleNext}>
                          {t("next")} <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          type="button"
                          onClick={handleSave}
                          disabled={isLoading}
                        >
                          {isLoading ? t("saving") : (
                            <>
                              {t("save")} <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                            </>
                          )}
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      )}
    </FormProvider>
  );
};

export default EditUser;
